import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { 
  Search, 
  Loader2, 
  Image as ImageIcon, 
  Star, 
  Heart,
  Home,
  User,
  Book,
  Apple,
  Sun,
  Cloud,
  Music,
  Palette,
  Shapes,
  X,
  Download,
  Plus
} from "lucide-react";

/**
 * ClipArtLibraryDialog - Pedagogical clipart library
 * Integrates ARASAAC pictograms and custom educational cliparts
 */

// ARASAAC API base URL
const ARASAAC_API = "https://api.arasaac.org/v1";

// Predefined categories for pedagogical use - RESTRUCTURED
const CATEGORIES = [
  // Pedagogic categories
  { id: "lecture", label: "Lecture", icon: "📖", color: "#3b82f6", keywords: ["lire", "lettre", "mot", "phrase", "son", "syllabe"] },
  { id: "maths", label: "Maths", icon: "🔢", color: "#10b981", keywords: ["nombre", "calcul", "addition", "géométrie", "mesure"] },
  { id: "consignes", label: "Consignes", icon: "📋", color: "#f59e0b", keywords: ["écouter", "écrire", "colorier", "découper", "coller"] },
  { id: "sciences", label: "Sciences", icon: "🔬", color: "#8b5cf6", keywords: ["observer", "expérience", "vivant", "corps", "planète"] },
  { id: "school", label: "École", icon: "📚", color: "#ef4444", keywords: ["classe", "professeur", "élève", "crayon", "bureau"] },
  // General categories
  { id: "animals", label: "Animaux", icon: "🐾", color: "#6b7280", keywords: ["animal", "chien", "chat", "oiseau", "poisson"] },
  { id: "people", label: "Personnes", icon: "👨‍👩‍👧", color: "#6b7280", keywords: ["garçon", "fille", "homme", "femme", "famille"] },
  { id: "emotions", label: "Émotions", icon: "😊", color: "#6b7280", keywords: ["content", "triste", "colère", "peur"] },
  { id: "nature", label: "Nature", icon: "🌳", color: "#6b7280", keywords: ["arbre", "fleur", "soleil", "pluie"] },
  { id: "objects", label: "Objets", icon: "📦", color: "#6b7280", keywords: ["table", "chaise", "livre", "stylo"] },
];

// Pedagogic functional blocks - pre-designed elements for worksheets
const PEDAGOGIC_BLOCKS = [
  // Labels / Étiquettes
  { 
    id: "label-blue", 
    name: "Étiquette bleue", 
    category: "labels",
    type: "label",
    preview: "div",
    style: { width: 120, height: 40, fill: "#3b82f6", stroke: "#1d4ed8", radius: 8 },
    defaultText: "Mot"
  },
  { 
    id: "label-green", 
    name: "Étiquette verte", 
    category: "labels",
    type: "label",
    preview: "div",
    style: { width: 120, height: 40, fill: "#10b981", stroke: "#059669", radius: 8 },
    defaultText: "Mot"
  },
  { 
    id: "label-orange", 
    name: "Étiquette orange", 
    category: "labels",
    type: "label",
    preview: "div",
    style: { width: 120, height: 40, fill: "#f59e0b", stroke: "#d97706", radius: 8 },
    defaultText: "Mot"
  },
  { 
    id: "label-purple", 
    name: "Étiquette violette", 
    category: "labels",
    type: "label",
    preview: "div",
    style: { width: 120, height: 40, fill: "#8b5cf6", stroke: "#7c3aed", radius: 8 },
    defaultText: "Mot"
  },
  { 
    id: "label-dotted", 
    name: "Étiquette pointillée", 
    category: "labels",
    type: "label",
    preview: "div",
    style: { width: 120, height: 40, fill: "#ffffff", stroke: "#94a3b8", radius: 8, dash: [5, 5] },
    defaultText: "..."
  },
  // Frames / Cadres
  { 
    id: "frame-simple", 
    name: "Cadre simple", 
    category: "frames",
    type: "frame",
    preview: "rect",
    style: { width: 200, height: 150, fill: "#ffffff", stroke: "#374151", strokeWidth: 2, radius: 0 }
  },
  { 
    id: "frame-rounded", 
    name: "Cadre arrondi", 
    category: "frames",
    type: "frame",
    preview: "rect",
    style: { width: 200, height: 150, fill: "#ffffff", stroke: "#374151", strokeWidth: 2, radius: 12 }
  },
  { 
    id: "frame-dashed", 
    name: "Cadre pointillé", 
    category: "frames",
    type: "frame",
    preview: "rect",
    style: { width: 200, height: 150, fill: "#ffffff", stroke: "#6b7280", strokeWidth: 2, radius: 0, dash: [8, 4] }
  },
  { 
    id: "frame-highlight", 
    name: "Cadre surligné", 
    category: "frames",
    type: "frame",
    preview: "rect",
    style: { width: 200, height: 150, fill: "#fef3c7", stroke: "#f59e0b", strokeWidth: 3, radius: 8 }
  },
  { 
    id: "frame-consigne", 
    name: "Cadre consigne", 
    category: "frames",
    type: "frame",
    preview: "rect",
    style: { width: 400, height: 60, fill: "#eff6ff", stroke: "#3b82f6", strokeWidth: 2, radius: 8 }
  },
  // Lines / Lignes
  { 
    id: "line-seyes", 
    name: "Lignes Seyes", 
    category: "lines",
    type: "seyes",
    preview: "lines",
    style: { width: 400, height: 120, lineCount: 3 }
  },
  { 
    id: "line-simple", 
    name: "Lignes simples", 
    category: "lines",
    type: "simplelines",
    preview: "lines",
    style: { width: 400, height: 80, lineCount: 4, lineSpacing: 20 }
  },
  // Tables / Tableaux
  { 
    id: "table-2x2", 
    name: "Tableau 2x2", 
    category: "tables",
    type: "table",
    preview: "table",
    style: { cols: 2, rows: 2, cellWidth: 100, cellHeight: 50, stroke: "#374151" }
  },
  { 
    id: "table-3x3", 
    name: "Tableau 3x3", 
    category: "tables",
    type: "table",
    preview: "table",
    style: { cols: 3, rows: 3, cellWidth: 80, cellHeight: 50, stroke: "#374151" }
  },
  { 
    id: "table-2col", 
    name: "2 colonnes", 
    category: "tables",
    type: "table",
    preview: "table",
    style: { cols: 2, rows: 4, cellWidth: 150, cellHeight: 40, stroke: "#374151" }
  },
  { 
    id: "table-header", 
    name: "Tableau avec en-tête", 
    category: "tables",
    type: "table",
    preview: "table",
    style: { cols: 3, rows: 4, cellWidth: 100, cellHeight: 40, stroke: "#374151", headerBg: "#e5e7eb" }
  },
  // Boxes / Boîtes
  { 
    id: "box-answer", 
    name: "Case réponse", 
    category: "boxes",
    type: "answerbox",
    preview: "rect",
    style: { width: 80, height: 40, fill: "#ffffff", stroke: "#374151", strokeWidth: 1 }
  },
  { 
    id: "box-checkbox", 
    name: "Case à cocher", 
    category: "boxes",
    type: "checkbox",
    preview: "rect",
    style: { width: 24, height: 24, fill: "#ffffff", stroke: "#374151", strokeWidth: 2 }
  },
  { 
    id: "box-number", 
    name: "Case nombre", 
    category: "boxes",
    type: "numberbox",
    preview: "rect",
    style: { width: 50, height: 50, fill: "#f8fafc", stroke: "#94a3b8", strokeWidth: 1, radius: 4 }
  },
];

const BLOCK_CATEGORIES = [
  { id: "labels", label: "Étiquettes", icon: "🏷️" },
  { id: "frames", label: "Cadres", icon: "⬜" },
  { id: "lines", label: "Lignes", icon: "📝" },
  { id: "tables", label: "Tableaux", icon: "📊" },
  { id: "boxes", label: "Cases", icon: "☑️" },
];

// Builtin SVG cliparts (fallback when API is unavailable)
const BUILTIN_CLIPARTS = [
  // Lecture
  { id: "book", name: "Livre", url: "https://api.iconify.design/twemoji/open-book.svg", category: "lecture" },
  { id: "pencil", name: "Crayon", url: "https://api.iconify.design/twemoji/pencil.svg", category: "lecture" },
  { id: "abc", name: "ABC", url: "https://api.iconify.design/twemoji/input-latin-letters.svg", category: "lecture" },
  { id: "speech", name: "Parler", url: "https://api.iconify.design/twemoji/speaking-head.svg", category: "lecture" },
  // Maths
  { id: "123", name: "123", url: "https://api.iconify.design/twemoji/input-numbers.svg", category: "maths" },
  { id: "plus", name: "Plus", url: "https://api.iconify.design/twemoji/plus.svg", category: "maths" },
  { id: "minus", name: "Moins", url: "https://api.iconify.design/twemoji/minus.svg", category: "maths" },
  { id: "ruler", name: "Règle", url: "https://api.iconify.design/twemoji/straight-ruler.svg", category: "maths" },
  { id: "triangular-ruler", name: "Équerre", url: "https://api.iconify.design/twemoji/triangular-ruler.svg", category: "maths" },
  // Consignes
  { id: "ear", name: "Écouter", url: "https://api.iconify.design/twemoji/ear.svg", category: "consignes" },
  { id: "eye", name: "Observer", url: "https://api.iconify.design/twemoji/eyes.svg", category: "consignes" },
  { id: "hand", name: "Écrire", url: "https://api.iconify.design/twemoji/writing-hand.svg", category: "consignes" },
  { id: "scissors", name: "Découper", url: "https://api.iconify.design/twemoji/scissors.svg", category: "consignes" },
  { id: "glue", name: "Coller", url: "https://api.iconify.design/twemoji/adhesive-bandage.svg", category: "consignes" },
  { id: "paintbrush", name: "Colorier", url: "https://api.iconify.design/twemoji/paintbrush.svg", category: "consignes" },
  // Sciences
  { id: "microscope", name: "Microscope", url: "https://api.iconify.design/twemoji/microscope.svg", category: "sciences" },
  { id: "magnet", name: "Aimant", url: "https://api.iconify.design/twemoji/magnet.svg", category: "sciences" },
  { id: "plant", name: "Plante", url: "https://api.iconify.design/twemoji/seedling.svg", category: "sciences" },
  { id: "globe", name: "Terre", url: "https://api.iconify.design/twemoji/globe-showing-europe-africa.svg", category: "sciences" },
  // School
  { id: "checkmark", name: "Correct", url: "https://api.iconify.design/twemoji/check-mark-button.svg", category: "school" },
  { id: "cross", name: "Faux", url: "https://api.iconify.design/twemoji/cross-mark.svg", category: "school" },
  { id: "question", name: "Question", url: "https://api.iconify.design/twemoji/question-mark.svg", category: "school" },
  { id: "lightbulb", name: "Idée", url: "https://api.iconify.design/twemoji/light-bulb.svg", category: "school" },
  { id: "trophy", name: "Trophée", url: "https://api.iconify.design/twemoji/trophy.svg", category: "school" },
  { id: "medal", name: "Médaille", url: "https://api.iconify.design/twemoji/sports-medal.svg", category: "school" },
  { id: "star", name: "Étoile", url: "https://api.iconify.design/twemoji/star.svg", category: "school" },
  // General categories
  { id: "heart", name: "Cœur", url: "https://api.iconify.design/twemoji/red-heart.svg", category: "emotions" },
  { id: "happy", name: "Content", url: "https://api.iconify.design/twemoji/grinning-face.svg", category: "emotions" },
  { id: "sad", name: "Triste", url: "https://api.iconify.design/twemoji/crying-face.svg", category: "emotions" },
  { id: "angry", name: "En colère", url: "https://api.iconify.design/twemoji/angry-face.svg", category: "emotions" },
  { id: "sun", name: "Soleil", url: "https://api.iconify.design/twemoji/sun.svg", category: "nature" },
  { id: "cloud", name: "Nuage", url: "https://api.iconify.design/twemoji/cloud.svg", category: "nature" },
  { id: "rain", name: "Pluie", url: "https://api.iconify.design/twemoji/cloud-with-rain.svg", category: "nature" },
  { id: "tree", name: "Arbre", url: "https://api.iconify.design/twemoji/deciduous-tree.svg", category: "nature" },
  { id: "flower", name: "Fleur", url: "https://api.iconify.design/twemoji/cherry-blossom.svg", category: "nature" },
  { id: "rainbow", name: "Arc-en-ciel", url: "https://api.iconify.design/twemoji/rainbow.svg", category: "nature" },
  { id: "cat", name: "Chat", url: "https://api.iconify.design/twemoji/cat-face.svg", category: "animals" },
  { id: "dog", name: "Chien", url: "https://api.iconify.design/twemoji/dog-face.svg", category: "animals" },
  { id: "bird", name: "Oiseau", url: "https://api.iconify.design/twemoji/bird.svg", category: "animals" },
  { id: "fish", name: "Poisson", url: "https://api.iconify.design/twemoji/fish.svg", category: "animals" },
  { id: "butterfly", name: "Papillon", url: "https://api.iconify.design/twemoji/butterfly.svg", category: "animals" },
  { id: "boy", name: "Garçon", url: "https://api.iconify.design/twemoji/boy.svg", category: "people" },
  { id: "girl", name: "Fille", url: "https://api.iconify.design/twemoji/girl.svg", category: "people" },
  { id: "house", name: "Maison", url: "https://api.iconify.design/twemoji/house.svg", category: "objects" },
  { id: "clock", name: "Horloge", url: "https://api.iconify.design/twemoji/alarm-clock.svg", category: "objects" },
  { id: "apple", name: "Pomme", url: "https://api.iconify.design/twemoji/red-apple.svg", category: "objects" },
];

export function ClipArtLibraryDialog({ open, onOpenChange, onSelectClipArt, onSelectBlock }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBlockCategory, setSelectedBlockCategory] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [activeTab, setActiveTab] = useState("builtin"); // builtin, blocks, arasaac, favorites

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem("clipart_favorites");
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (e) {
        console.error("Error loading favorites:", e);
      }
    }
  }, []);

  // Search ARASAAC pictograms
  const searchArasaac = useCallback(async (query) => {
    if (!query || query.length < 2) return;
    
    setLoading(true);
    try {
      // Search in French
      const response = await fetch(`${ARASAAC_API}/pictograms/fr/search/${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        // Map to our format
        const results = data.slice(0, 30).map(item => ({
          id: `arasaac_${item._id}`,
          name: item.keywords?.[0]?.keyword || query,
          url: `https://static.arasaac.org/pictograms/${item._id}/${item._id}_500.png`,
          source: "arasaac",
          originalId: item._id
        }));
        setSearchResults(results);
      }
    } catch (error) {
      console.error("ARASAAC search error:", error);
      setSearchResults([]);
    }
    setLoading(false);
  }, []);

  // Handle search
  const handleSearch = useCallback((e) => {
    e.preventDefault();
    if (activeTab === "arasaac") {
      searchArasaac(searchQuery);
    }
  }, [searchQuery, activeTab, searchArasaac]);

  // Filter builtin cliparts by category or search
  const filteredBuiltin = BUILTIN_CLIPARTS.filter(clip => {
    if (selectedCategory) {
      return clip.category === selectedCategory;
    }
    if (searchQuery) {
      return clip.name.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  // Filter pedagogic blocks by category
  const filteredBlocks = PEDAGOGIC_BLOCKS.filter(block => {
    if (selectedBlockCategory) {
      return block.category === selectedBlockCategory;
    }
    return true;
  });

  // Add to favorites
  const toggleFavorite = (clipart) => {
    const newFavorites = favorites.some(f => f.id === clipart.id)
      ? favorites.filter(f => f.id !== clipart.id)
      : [...favorites, clipart];
    
    setFavorites(newFavorites);
    localStorage.setItem("clipart_favorites", JSON.stringify(newFavorites));
  };

  // Handle clipart selection
  const handleSelect = (clipart) => {
    onSelectClipArt?.(clipart);
    onOpenChange(false);
  };

  // Handle block selection
  const handleSelectBlock = (block) => {
    onSelectBlock?.(block);
    onOpenChange(false);
  };

  // Render block preview
  const BlockPreview = ({ block }) => {
    const { style } = block;
    
    if (block.type === "label") {
      return (
        <div
          className="flex items-center justify-center text-white text-xs font-medium"
          style={{
            width: Math.min(style.width, 100),
            height: Math.min(style.height, 35),
            backgroundColor: style.fill,
            border: `2px solid ${style.stroke}`,
            borderRadius: style.radius || 0,
            borderStyle: style.dash ? "dashed" : "solid",
            color: style.fill === "#ffffff" ? "#374151" : "#ffffff",
          }}
        >
          {block.defaultText || "Texte"}
        </div>
      );
    }
    
    if (block.type === "frame" || block.type === "answerbox" || block.type === "checkbox" || block.type === "numberbox") {
      return (
        <div
          style={{
            width: Math.min(style.width, 100),
            height: Math.min(style.height, 60),
            backgroundColor: style.fill,
            border: `${style.strokeWidth || 2}px ${style.dash ? "dashed" : "solid"} ${style.stroke}`,
            borderRadius: style.radius || 0,
          }}
        />
      );
    }
    
    if (block.type === "table") {
      const cellW = Math.min(25, 80 / style.cols);
      const cellH = Math.min(20, 60 / style.rows);
      return (
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${style.cols}, ${cellW}px)`,
            gridTemplateRows: `repeat(${style.rows}, ${cellH}px)`,
          }}
        >
          {[...Array(style.cols * style.rows)].map((_, i) => (
            <div
              key={i}
              style={{
                border: `1px solid ${style.stroke}`,
                backgroundColor: i < style.cols && style.headerBg ? style.headerBg : "white",
              }}
            />
          ))}
        </div>
      );
    }
    
    if (block.type === "seyes" || block.type === "simplelines") {
      return (
        <div className="flex flex-col gap-1 w-20">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-3 border-b border-blue-300" />
          ))}
        </div>
      );
    }
    
    return <div className="w-16 h-10 bg-slate-100 rounded" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-pink-500" />
            Bibliotheque de cliparts
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b pb-2 flex-wrap">
          <button
            onClick={() => setActiveTab("builtin")}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === "builtin" 
                ? "bg-pink-100 text-pink-700" 
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Cliparts
          </button>
          <button
            onClick={() => setActiveTab("blocks")}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === "blocks" 
                ? "bg-indigo-100 text-indigo-700" 
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Blocs pedagogiques
          </button>
          <button
            onClick={() => setActiveTab("arasaac")}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === "arasaac" 
                ? "bg-blue-100 text-blue-700" 
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            ARASAAC
          </button>
          <button
            onClick={() => setActiveTab("favorites")}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-1 ${
              activeTab === "favorites" 
                ? "bg-yellow-100 text-yellow-700" 
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            <Star className="w-4 h-4" />
            Favoris ({favorites.length})
          </button>
        </div>

        {/* Search bar - only for cliparts tabs */}
        {(activeTab === "builtin" || activeTab === "arasaac") && (
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={activeTab === "arasaac" ? "Rechercher un pictogramme..." : "Filtrer les cliparts..."}
                className="pl-10"
              />
            </div>
            {activeTab === "arasaac" && (
              <Button type="submit" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rechercher"}
              </Button>
            )}
          </form>
        )}

        {/* Categories for builtin cliparts */}
        {activeTab === "builtin" && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                !selectedCategory 
                  ? "bg-pink-500 text-white" 
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Tous
            </button>
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                  selectedCategory === cat.id 
                    ? "text-white" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                style={selectedCategory === cat.id ? { backgroundColor: cat.color || "#ec4899" } : {}}
              >
                <span>{cat.icon}</span>
                {cat.label}
              </button>
            ))}
          </div>
        )}

        {/* Categories for blocks */}
        {activeTab === "blocks" && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedBlockCategory(null)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                !selectedBlockCategory 
                  ? "bg-indigo-500 text-white" 
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Tous
            </button>
            {BLOCK_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedBlockCategory(cat.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                  selectedBlockCategory === cat.id 
                    ? "bg-indigo-500 text-white" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <span>{cat.icon}</span>
                {cat.label}
              </button>
            ))}
          </div>
        )}

        {/* Clipart grid */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Blocks grid - different layout for blocks */}
          {activeTab === "blocks" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 p-2">
              {filteredBlocks.map(block => (
                <button
                  key={block.id}
                  onClick={() => handleSelectBlock(block)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 transition-all group"
                  data-testid={`block-${block.id}`}
                >
                  <div className="h-16 flex items-center justify-center">
                    <BlockPreview block={block} />
                  </div>
                  <span className="text-xs font-medium text-slate-600 group-hover:text-indigo-700">
                    {block.name}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Cliparts grid */}
          {activeTab !== "blocks" && (
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3 p-2">
              {/* Builtin cliparts */}
              {activeTab === "builtin" && filteredBuiltin.map(clip => (
                <ClipArtItem
                  key={clip.id}
                  clipart={clip}
                  isFavorite={favorites.some(f => f.id === clip.id)}
                  onSelect={handleSelect}
                  onToggleFavorite={toggleFavorite}
                />
              ))}

            {/* ARASAAC results */}
            {activeTab === "arasaac" && (
              <>
                {loading && (
                  <div className="col-span-full flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  </div>
                )}
                {!loading && searchResults.length === 0 && (
                  <div className="col-span-full text-center py-12 text-slate-500">
                    <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Recherchez un mot pour trouver des pictogrammes ARASAAC</p>
                    <p className="text-sm">Ex: chat, manger, content, école...</p>
                  </div>
                )}
                {!loading && searchResults.map(clip => (
                  <ClipArtItem
                    key={clip.id}
                    clipart={clip}
                    isFavorite={favorites.some(f => f.id === clip.id)}
                    onSelect={handleSelect}
                    onToggleFavorite={toggleFavorite}
                  />
                ))}
              </>
            )}

            {/* Favorites */}
            {activeTab === "favorites" && (
              <>
                {favorites.length === 0 && (
                  <div className="col-span-full text-center py-12 text-slate-500">
                    <Star className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucun favori pour l'instant</p>
                    <p className="text-sm">Cliquez sur l'etoile pour ajouter aux favoris</p>
                  </div>
                )}
                {favorites.map(clip => (
                  <ClipArtItem
                    key={clip.id}
                    clipart={clip}
                    isFavorite={true}
                    onSelect={handleSelect}
                    onToggleFavorite={toggleFavorite}
                  />
                ))}
              </>
            )}
            </div>
          )}
        </div>

        {/* Footer with ARASAAC attribution */}
        {activeTab === "arasaac" && (
          <div className="text-xs text-slate-500 text-center pt-2 border-t">
            Pictogrammes par <a href="https://arasaac.org" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">ARASAAC</a> - Licence CC BY-NC-SA
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * ClipArtItem - Individual clipart in the grid
 */
function ClipArtItem({ clipart, isFavorite, onSelect, onToggleFavorite }) {
  const [imageError, setImageError] = useState(false);

  return (
    <div className="group relative">
      <button
        onClick={() => onSelect(clipart)}
        className="w-full aspect-square bg-white border-2 border-slate-200 rounded-xl p-2 hover:border-pink-400 hover:shadow-lg transition-all flex items-center justify-center"
      >
        {!imageError ? (
          <img
            src={clipart.url}
            alt={clipart.name}
            className="w-full h-full object-contain"
            onError={() => setImageError(true)}
          />
        ) : (
          <ImageIcon className="w-8 h-8 text-slate-300" />
        )}
      </button>
      
      {/* Favorite button */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleFavorite(clipart); }}
        className={`absolute top-1 right-1 p-1 rounded-full transition-colors ${
          isFavorite 
            ? "bg-yellow-400 text-white" 
            : "bg-white/80 text-slate-400 opacity-0 group-hover:opacity-100"
        }`}
      >
        <Star className="w-3 h-3" fill={isFavorite ? "currentColor" : "none"} />
      </button>

      {/* Name tooltip */}
      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
        {clipart.name}
      </div>
    </div>
  );
}

/**
 * ClipArtButton - Trigger button for the library
 */
export function ClipArtLibraryButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-11 h-11 rounded-xl bg-pink-100 text-pink-600 hover:bg-pink-200 flex items-center justify-center"
      title="Bibliotheque de cliparts"
    >
      <ImageIcon size={20} />
    </button>
  );
}

export default ClipArtLibraryDialog;
